import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import swal from "sweetalert";
import { getConnectedAccounts, createNewCard } from "../../../../../services/payment";
import {
  getSubscription,
  updateCardActive,
  deleteCard,
  upgradePlan,
  downgradePlan,
  previewPlanChange,
} from "../../../../../services/subscription";
import { getProfile, updateBusinessProfile } from "../../../../../services/profile";
import { isAppInstalled, appMonthlyPrice } from "../../../../../utils/appEntitlements";
import { appTheme } from "../../../../../config/appTheme";
import { DEFAULT_THEME_KEY } from "../../../../../config/themePresets";
import { getPackages } from "../../../../../services/package";
import { getAllStaff } from "../../../../../services/staff";
import { getActiveFacilitiesCount } from "../../../../../services/facility";
import { getSkaiTokenUsage } from "../../../../../services/skaiTokens";
import SkaiCreditsCard from "./SkaiCreditsCard";
import { useSkaiAddon } from "./useSkaiAddon";
import "../settings.css";

// Plans that keep white-label / Design features (mirrors SettingsShell gating).
// Downgrading below these strips branding and reverts the theme to standard.
const TOP_TIER_PLANS = ["agency", "gold", "scale"];

/* ------------------------------------------------------------------ *
 * Billing & plan panel
 *
 * Reuses the existing Payment/index.jsx data + flows:
 *   - getConnectedAccounts()  -> { accounts, upcomingInvoice }   (real)
 *   - getSubscription()       -> live subscription snapshot       (real)
 *   - profile.subscription    -> active plan/price/renewal        (real)
 *   - card add/update         -> navigate("/add-payment", ...)    (real)
 *   - card delete / default   -> deleteCard / updateCardActive    (real)
 *   - plan switch / seats      -> existing upgrade flow at
 *                                 /home/settings?selectedTab=upgrade (real)
 *
 * NEW monetization pieces that have no dedicated backing flow yet
 * (per-add-on toggle purchase, in-modal proration confirm) render real
 * status from the data above and route to the existing flow; where no
 * flow is reachable they are a clearly-disabled "Coming soon" — never a
 * faked success.
 * ------------------------------------------------------------------ */

// Bold filled tier emblems (replaces the emoji medals). A duotone look — the
// solid glyph in the tier color plus a soft inset highlight — reads premium and
// themes cleanly via currentColor on the .plan-ico badge.
const PLAN_GLYPHS = {
  // Lightning — "get started".
  bolt: <path d="M13 2 4 14h6l-1 8 9-12h-6l1-8Z" />,
  // Star — the step up.
  star: (
    <path d="M12 2.2l2.92 6.06 6.58.78-4.86 4.5 1.27 6.5L12 17.3 6.02 20.04l1.27-6.5L2.5 9.04l6.58-.78L12 2.2Z" />
  ),
  // Gem — refined mid/upper tier.
  gem: (
    <path d="M6.2 2.5h11.6l3.7 5.5L12 21.5 .5 8 4.2 2.5h2Z M2.8 8h18.4M8.6 8 12 21M15.4 8 12 21M8.6 8 6.2 2.5M15.4 8l2.4-5.5" />
  ),
  // Crown — top tier.
  crown: (
    <path d="M2.6 7.2l4.4 3.1L12 3.8l5 6.5 4.4-3.1-1.7 11.1a1 1 0 0 1-1 .85H5.3a1 1 0 0 1-1-.85L2.6 7.2Z" />
  ),
};

// Pick a glyph + tone for a plan by its rank in the cost-sorted list. Top plan
// is always the crown/gold; the rest ramp up from bolt.
const planVisual = (rank, total) => {
  if (rank >= total - 1) return { glyph: "crown", tone: "gold" };
  const seq = [
    { glyph: "bolt", tone: "bronze" },
    { glyph: "star", tone: "slate" },
    { glyph: "gem", tone: "violet" },
  ];
  return seq[Math.min(rank, seq.length - 1)];
};

const PlanIcon = ({ glyph }) => {
  const isGem = glyph === "gem";
  return (
    <svg
      width="27"
      height="27"
      viewBox="0 0 24 24"
      fill={isGem ? "none" : "currentColor"}
      stroke={isGem ? "currentColor" : "none"}
      strokeWidth={isGem ? 1.5 : 0}
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {PLAN_GLYPHS[glyph]}
    </svg>
  );
};

// Build a feature summary line from the real package fields.
const planFeatures = (p) => {
  const unlimited = (v) =>
    v == null || Number(v) >= 9999 || String(v).toLowerCase() === "unlimited"
      ? "Unlimited"
      : v;
  const parts = [
    `${unlimited(p?.staff)} Staff`,
    `${unlimited(p?.facility)} ${Number(p?.facility) === 1 ? "Facility" : "Facilities"}`,
    `${unlimited(p?.patient)} Clients`,
    "Notes & Reports",
  ];
  return parts.join(" · ");
};

// Canonical NEW add-on prices (design contract).
const ADDON_PRICES = {
  skai: 49,
  evv: 0, // EVV is installed/included by default for every org (no charge)
  calendar: 80, // OD-1200: $80/mo, waived ($0) for grandfathered Time Management orgs
  clients: 24,
  staff: 5,
};

// Money formatter: drop cents when the amount is whole ($550), and only show
// them when there's an actual fractional part ($550.50). Keeps every amount on
// the billing page consistent.
const fmtMoney = (n) => {
  if (n == null || Number.isNaN(Number(n))) return "-";
  const num = Number(n);
  return Number.isInteger(num) ? `$${num}` : `$${num.toFixed(2)}`;
};

const CloseIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#525252"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const TrashIcon = ({ stroke }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke={stroke}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </svg>
);

const cardBrandClass = (data) => {
  if (!data?.creditCard) return "bank";
  const num = (data?.cardNumber || "").replace(/\s/g, "");
  if (num.startsWith("4")) return "visa";
  if (/^5[1-5]/.test(num) || /^2[2-7]/.test(num)) return "mc";
  return "visa";
};

const cardBrandLabel = (data) => {
  const cls = cardBrandClass(data);
  if (cls === "bank") return "BANK";
  if (cls === "mc") return "MC";
  return "VISA";
};

const last4 = (val) => {
  const s = String(val || "");
  return s.length >= 4 ? s.slice(-4) : s;
};

export default function BillingPlanPanel({ profile: profileProp, role }) {
  const navigate = useNavigate();
  const location = useLocation();
  // Deep-link support: a `planToken` query param (e.g. an "update your plan"
  // email link) auto-opens the update-plan modal below once this admin panel
  // is ready.
  const hasPlanToken = !!new URLSearchParams(location.search).get("planToken");

  // Prefer redux profile (single source of truth, kept fresh by getProfile),
  // fall back to the prop the shell handed us.
  const reduxProfile = useSelector((state) => state?.profile?.profile);
  const profile = reduxProfile || profileProp;

  const isAdmin = (role || profile?.user?.type) !== "staff";

  // SkAi add-on subscription state (shared with the SkAi card & Apps page).
  const { onMonthly: skaiActive, pendingCancel: skaiPendingCancel, accessUntil: skaiAccessUntil, isUnlimited: skaiUnlimited, tokenRemaining: skaiTokenBalance, monthlyPrice: skaiMonthlyPrice, cancelLoading: skaiCancelLoading, cancel: cancelSkai, cancelModal: skaiCancelModal } = useSkaiAddon(profile);

  const [connected, setConnected] = useState([]);
  const [upcomingInvoice, setUpcomingInvoice] = useState(null);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [actionLoading, setActionLoading] = useState(null); // card id mid-flight
  const [liveSub, setLiveSub] = useState(null);

  // Update-plan modal state
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [cycle, setCycle] = useState("month");
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [packages, setPackages] = useState([]);
  const [planChangeLoading, setPlanChangeLoading] = useState(false);
  // Pending plan-change confirmation (on-brand modal): null | { kind, target, … }
  const [planConfirm, setPlanConfirm] = useState(null);
  // Final "authorize the charge" gate before we actually change the plan.
  const [planPayConfirm, setPlanPayConfirm] = useState(false);
  // On-brand success view shown in-modal after a plan change (replaces swal).
  const [planSuccess, setPlanSuccess] = useState(null);

  // Counts used for the downgrade guard (same source as SettingsPackageCard).
  const activeStaffCount = useSelector((state) => state?.staff?.activeStaffCount);
  const activePatientsCount = useSelector(
    (state) => state?.patient?.activePatientsCount
  );
  const allFacilities = useSelector((state) => state?.facility?.allFacilities);

  // Active-facility count for the downgrade guard. Fetched by this panel (not
  // read from the shared `allFacilities` store, which is a full-list array that
  // varies with navigation) so the guard counts only active facilities.
  const [activeFacilityCount, setActiveFacilityCount] = useState(null);

  // Subscription details are collapsed by default — expanded on demand.
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Add-card modal state (Stripe — POST /payment/create-new-card)
  const [cardModalOpen, setCardModalOpen] = useState(false);
  const [connectingCard, setConnectingCard] = useState(false);
  const [card, setCard] = useState({
    name: "",
    cardNumber: "",
    cardMonth: "",
    cardYear: "",
    CVV: "",
  });
  const setCardField = (k) => (e) =>
    setCard((c) => ({ ...c, [k]: e.target.value }));

  const subscription = profile?.subscription;
  const activePackage = subscription?.packageId;

  const loadAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const res = await getConnectedAccounts();
      setConnected(res?.accounts || []);
      setUpcomingInvoice(res?.upcomingInvoice || null);
    } catch (e) {
      setConnected([]);
      setUpcomingInvoice(null);
    }
    setLoadingAccounts(false);
  };

  // Add a card via the existing Stripe flow (POST /payment/create-new-card),
  // the same one the standalone Add-payment page uses — just in a modal.
  const handleAddCard = async () => {
    const expMonth = Number(card.cardMonth);
    const expYear = Number(card.cardYear);
    const now = new Date();
    const curMonth = now.getMonth() + 1;
    const curYear = now.getFullYear() % 100;
    if (!card.name?.trim() || !card.cardNumber?.trim() || !card.CVV?.trim()) {
      swal("Missing details", "Please fill in all card fields.", "error");
      return;
    }
    if (
      expMonth < 1 || expMonth > 12 ||
      expYear < curYear || (expYear === curYear && expMonth < curMonth)
    ) {
      swal("Invalid Card Expiry", "Please enter a valid expiration month and year.", "error");
      return;
    }
    const obj = {
      name: card.name,
      cardNumber: card.cardNumber,
      CVV: card.CVV,
      cardExp: `${card.cardMonth}/${card.cardYear}`,
      userId: profile?.user?.id,
      email: profile?.user?.email,
    };
    setConnectingCard(true);
    createNewCard(obj, async (error) => {
      setConnectingCard(false);
      if (!error) {
        setCardModalOpen(false);
        setCard({ name: "", cardNumber: "", cardMonth: "", cardYear: "", CVV: "" });
        await loadAccounts();
      }
    });
  };

  useEffect(() => {
    if (!isAdmin) return;
    loadAccounts();
    // Pull a fresh subscription snapshot from the existing endpoint.
    getSubscription(() => {}).then((data) => {
      if (data) setLiveSub(data?.subscription || data);
    });
    // Real plan catalogue + active staff count (for the downgrade guard) —
    // same data the working Payment page loads.
    (async () => {
      try {
        const resp = await getPackages(profile?.user?.id);
        const list = (resp?.packages || [])
          .slice()
          .sort((a, b) => Number(a.cost) - Number(b.cost));
        setPackages(list);
      } catch (e) {
        setPackages([]);
      }
    })();
    getAllStaff("activeStaff");
    if (isAdmin) {
      getActiveFacilitiesCount().then((n) => setActiveFacilityCount(n));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  // Other surfaces (SkAi subscribe/cancel, seat add-ons) change the upcoming
  // invoice — they fire "billing:refresh" so the figures update without a
  // page reload.
  useEffect(() => {
    if (!isAdmin) return;
    const refresh = () => {
      loadAccounts();
      getSubscription(() => {}).then((data) => {
        if (data) setLiveSub(data?.subscription || data);
      });
    };
    window.addEventListener("billing:refresh", refresh);
    return () => window.removeEventListener("billing:refresh", refresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  // ---- Active subscription (real) ----
  const planName = activePackage?.name || liveSub?.packageId?.name || "-";
  const isActive =
    subscription?.isActive ?? liveSub?.isActive ?? false;
  const subType =
    subscription?.subscriptionType || liveSub?.subscriptionType || "month";
  const cycleLabel = subType === "year" ? "yearly" : "monthly";
  const renewalDate = subscription?.expiredAt || liveSub?.expiredAt;
  const renewalStr = renewalDate
    ? moment(renewalDate).format("MMMM D, YYYY")
    : "-";
  const planCost =
    activePackage?.cost != null
      ? Number(activePackage.cost)
      : liveSub?.packageId?.cost != null
      ? Number(liveSub.packageId.cost)
      : null;
  const planCostStr =
    planCost != null ? fmtMoney(planCost) : "-";

  const periodSuffix = subType === "year" ? "yr" : "mo";

  // ---- Real add-on seats (purchased counts × the plan's per-seat price) ----
  const pkg = activePackage || liveSub?.packageId || {};
  const planStaffLimit = Number(pkg.staff) || 0;
  const planClientLimit = Number(pkg.patient) || 0;
  const addStaffCount = Number(profile?.user?.additionalStaffCount) || 0;
  const addClientCount = Number(profile?.user?.additionalClientCount) || 0;
  const staffSeatPrice =
    Number(profile?.user?.additionalStaffCost) || Number(pkg.additionalStaff) || 0;
  const clientSeatPrice =
    Number(profile?.user?.additionalClientCost) || Number(pkg.additionalClient) || 0;
  const addStaffCost = addStaffCount * staffSeatPrice;
  const addClientCost = addClientCount * clientSeatPrice;

  // Gold bundles the apps (SkAi + EVV) at no extra charge; other plans pay for
  // them as add-ons that DO count toward the monthly total.
  const isGoldPlan = String(planName).toLowerCase() === "gold";
  // Calendar is a separately-installed app charge (OD-1200: $80/mo, waived to
  // $0 for orgs grandfathered as active Time Management accounts). Uninstalling
  // it unsubscribes from the charge, so it drops out of Subscription Details.
  const calendarInstalled = isAppInstalled(profile, "calendar");
  const calendarMonthly = calendarInstalled ? appMonthlyPrice(profile, "calendar") : 0;

  // Subscription total = base plan + billed add-ons + real add-on seats.
  //  - SkAi: only the monthly add-on when actually subscribed (included on Gold).
  //  - EVV: included on Gold; on other plans the $79/mo add-on is billed.
  //  - Calendar: billed only when installed and not grandfathered free.
  //  - Seats: real purchased additional staff/clients.
  const addonsTotal =
    (skaiActive ? skaiMonthlyPrice || 0 : 0) +
    (isGoldPlan ? 0 : ADDON_PRICES.evv) +
    calendarMonthly +
    addStaffCost +
    addClientCost;
  const subscriptionTotal = (planCost != null ? planCost : 0) + addonsTotal;
  const totalStr = fmtMoney(subscriptionTotal);

  // Active card for upcoming-billing method line
  const activeCard = useMemo(
    () => (connected || []).find((c) => c?.isActive),
    [connected]
  );

  // ---- Plan switch routing (reuse existing upgrade/paynow flow) ----
  const goToUpgradeFlow = (extraState = {}) => {
    // The existing Payment panel (SettingsPackageCard -> services/subscription
    // upgradePlan/selectPlan/paynow) lives behind ?selectedTab=upgrade.
    navigate("/home/settings?selectedTab=upgrade", {
      state: { selectedTab: "upgrade", ...extraState },
    });
  };

  // The currently-subscribed package (single source of truth for current/changed).
  const activeId = activePackage || liveSub?.packageId || null;

  // Real plans for the chosen cycle (exclude Skypoint + the legacy Free plan
  // unless the user is actually on Free), cost-sorted — matches Payment page.
  const modalPlans = useMemo(() => {
    return (packages || [])
      .filter(
        (p) =>
          p?.type !== "skypoint" &&
          p?.duration === cycle &&
          (p?.name !== "Free" || activeId?.name === "Free")
      )
      .sort((a, b) => Number(a.cost) - Number(b.cost));
  }, [packages, cycle, activeId?.name]);

  const openPlanModal = () => {
    // Seed selection to the active package so confirm stays disabled until changed.
    setSelectedPlanId(activeId?.id || null);
    setCycle(subType === "year" ? "year" : "month");
    setPlanConfirm(null);
    setPlanPayConfirm(false);
    setPlanModalOpen(true);
  };

  const closePlanModal = () => {
    if (planChangeLoading) return;
    setPlanModalOpen(false);
    setPlanConfirm(null);
    setPlanPayConfirm(false);
    setPlanSuccess(null);
  };

  // No package on the profile at all (brand-new client, never subscribed) —
  // there's nothing to show on the hero card, so route straight to plan
  // selection instead of rendering "-"/"Inactive" placeholders.
  const hasNoPlan = !activeId;

  // Auto-open the plan-picker modal once the plan catalogue has loaded, either
  // deep-linked via `planToken` or because this admin has no plan yet.
  useEffect(() => {
    if ((hasPlanToken || hasNoPlan) && isAdmin && packages.length > 0) {
      openPlanModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPlanToken, hasNoPlan, isAdmin, packages.length]);

  const planChanged = selectedPlanId && selectedPlanId !== activeId?.id;

  const refreshAfterChange = async () => {
    await getProfile();
    getSubscription(() => {}).then((data) => {
      if (data) setLiveSub(data?.subscription || data);
    });
    loadAccounts();
    // A plan change also changes the SkAi monthly token allotment (Bronze 100 /
    // Silver 500 / Gold unlimited), so refresh the token usage or the SkAi card
    // shows a stale balance until the panel is remounted.
    const uid = profile?.user?.id;
    if (uid) getSkaiTokenUsage(uid);
  };

  // Decide upgrade vs downgrade, run the downgrade usage guard, then open the
  // on-brand confirm modal (replaces the old SweetAlert confirm).
  const confirmPlanChange = () => {
    if (!planChanged) return;
    const target = modalPlans.find((p) => p?.id === selectedPlanId);
    if (!target) return;

    const subscriptionType = cycle === "year" ? "year" : "month";
    const isMonthToYearUpgrade =
      activeId?.duration === "month" && subscriptionType === "year";

    // Same upgrade/downgrade conditions as SettingsPackageCard.onClick.
    const isDowngrade =
      (Number(target?.level) < Number(activeId?.level) &&
        activeId?.duration === "month" &&
        subscriptionType === "year") ||
      !(Number(target?.cost) > Number(activeId?.cost));

    // Downgrade usage guard — block if current usage exceeds the target limits.
    if (isDowngrade) {
      // Count active facilities (fetched by this panel). Fall back to the shared
      // store list length only if the fetch has not resolved yet.
      const currentFacilityCount =
        activeFacilityCount != null
          ? activeFacilityCount
          : Array.isArray(allFacilities)
          ? allFacilities.length
          : allFacilities?.count || 0;
      const targetFacilityLimit = target?.facility || 0;
      const staffLimit =
        Number(target?.staff) +
          (target?.name === "Starter"
            ? 0
            : profile?.user?.additionalStaffCount || 0) || 0;
      const patientLimit =
        Number(target?.patient) +
          (target?.name === "Starter"
            ? 0
            : profile?.user?.additionalClientCount || 0) || 0;
      if (
        currentFacilityCount > targetFacilityLimit ||
        activeStaffCount > staffLimit ||
        activePatientsCount > patientLimit
      ) {
        swal({
          title: "Downgrade Not Allowed",
          text: `You currently have more ${
            activeStaffCount > staffLimit ? "Staff," : ""
          } ${
            currentFacilityCount > targetFacilityLimit ? "Facilities," : ""
          } ${
            activePatientsCount > patientLimit ? "Clients" : ""
          } than this downgraded package supports. Please deactivate any non active ${
            activeStaffCount > staffLimit ? "Staff," : ""
          } ${
            currentFacilityCount > targetFacilityLimit ? "Facilities," : ""
          } ${
            activePatientsCount > patientLimit ? "Clients" : ""
          } to allow for this downgrade`,
          icon: "warning",
          buttons: ["Cancel", "OK"],
        });
        return;
      }
    }

    // Open the confirm view immediately. For an upgrade, the exact "due today"
    // proration comes from the server (POST /payment/preview-plan-change), which
    // shares the same calculation as the live charge — no client-side replica.
    setPlanPayConfirm(false);
    setPlanConfirm({
      kind: isDowngrade ? "downgrade" : "upgrade",
      target,
      subscriptionType,
      isMonthToYearUpgrade,
      prorated: null,
      remainingDays: null,
      breakdown: null,
      proratedLoading: !isDowngrade,
    });

    if (!isDowngrade) {
      previewPlanChange({
        packageId: target?.id,
        subscriptionType,
        monthToYear: isMonthToYearUpgrade,
      }).then((res) => {
        setPlanConfirm((prev) =>
          prev && prev.target?.id === target?.id
            ? {
                ...prev,
                prorated: res?.amount ?? null,
                remainingDays: res?.remainingDays ?? null,
                breakdown: res?.breakdown ?? null,
                proratedLoading: false,
              }
            : prev
        );
      });
    }
  };

  // Actually perform the plan change once confirmed in the modal — same
  // services/subscription payload shape as SettingsPackageCard.
  const runPlanChange = async () => {
    if (!planConfirm) return;
    const { kind, target, subscriptionType, isMonthToYearUpgrade } = planConfirm;
    setPlanChangeLoading(true);
    let response;
    if (kind === "upgrade") {
      response = await upgradePlan({
        packageId: target?.id,
        stripePlan: target?.stripeId,
        upgrade: true,
        subscriptionType,
        ...(isMonthToYearUpgrade && { monthToYear: true }),
      });
    } else {
      response = await downgradePlan({
        packageId: target?.id,
        stripePlan: target?.stripeId,
        upgrade: false,
        subscriptionType,
      });
    }
    setPlanChangeLoading(false);
    if (response) {
      // Downgrading below a top-tier plan removes white-label features: revert
      // the theme to the Ogiso standard design and clear brand customization.
      if (
        kind === "downgrade" &&
        !TOP_TIER_PLANS.includes(String(target?.name || "").toLowerCase())
      ) {
        appTheme.set(DEFAULT_THEME_KEY);
        const wl = profile?.business?.whiteLabel;
        if (wl && (wl.enabled || wl.primaryColor)) {
          try {
            await updateBusinessProfile({
              whiteLabel: { ...wl, enabled: false, primaryColor: "", accentColor: "" },
            });
          } catch (e) {
            /* profile refresh below still reflects the plan change */
          }
        }
      }
      setPlanSuccess({
        kind,
        name: target?.name,
        cost: target?.cost,
        cycle: subscriptionType === "year" ? "yr" : "mo",
      });
      setPlanConfirm(null);
      await refreshAfterChange();
    }
  };

  // ---- Payment methods ---- (Add card opens an in-page Stripe modal)
  const addCard = () => setCardModalOpen(true);

  const setDefaultCard = (data) => {
    const isTrial = Number(activePackage?.cost) === 0;
    swal({
      title: `Set default card for "${planName}" plan`,
      text: "Make this card the default for future renewals?",
      icon: "warning",
      dangerMode: !isTrial,
      buttons: ["Cancel", "Set as default"],
    }).then(async (ok) => {
      if (!ok || isTrial) return;
      setActionLoading(data.id);
      try {
        const res = await updateCardActive({
          stripeSubscriptionId: subscription?.stripeSubscriptionId,
          customer: data?.stripeCustomerId,
          default_source: data?.default_source,
        });
        if (res?.status === 200) {
          await loadAccounts();
          await getProfile();
        }
      } catch (e) {
        /* service surfaces its own error toast */
      }
      setActionLoading(null);
    });
  };

  const removeCard = (data) => {
    if (data?.isActive) return; // default card cannot be deleted
    swal({
      title: "Delete payment method",
      text: `Remove the card ending in ${last4(
        data?.cardNumber
      )}? Future renewals will not be charged to this account.`,
      icon: "warning",
      dangerMode: true,
      buttons: ["Cancel", "Delete card"],
    }).then(async (ok) => {
      if (!ok) return;
      setActionLoading(data.id);
      try {
        const res = await deleteCard(data.id, data.stripeCustomerId);
        if (res?.status === 200) await loadAccounts();
      } catch (e) {
        /* handled in service */
      }
      setActionLoading(null);
    });
  };

  // "Manage seats"/"Add seats" go where seats are actually used and bought:
  // the Staff page for staff seats, the Patients page for client seats (both
  // prompt the seat add-on purchase when the plan limit is hit).
  const manageSeats = (seatType) => {
    navigate(seatType === "staff" ? "/home/staff" : "/home/patient");
  };

  // Real add-on seat row: shows included + added = total, the per-seat price,
  // the actual cost, and a clear empty state when no extra seats are purchased.
  const renderSeatRow = ({ label, type, included, added, perSeat, cost }) => (
    <div className="addon-row">
      <div>
        <div className="addon-name">{label}</div>
        <div className="addon-sub">
          {added > 0
            ? `${included} included + ${added} added = ${included + added} total · $${perSeat}/seat`
            : `${included} included · no additional seats`}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div
          className="addon-price"
          style={added > 0 ? undefined : { color: "var(--muted)", fontWeight: 600 }}
        >
          {fmtMoney(cost)} / mo
        </div>
        <button
          className="btn sm manage-seats-btn"
          data-seat-type={type}
          onClick={() => manageSeats(type)}
        >
          {added > 0 ? "Manage seats" : "Add seats"}
        </button>
      </div>
    </div>
  );

  // ---- Upcoming billing (real) ----
  const nextPaymentStr =
    upcomingInvoice?.nextPayment != null
      ? fmtMoney(upcomingInvoice.nextPayment)
      : planCost != null
      ? planCostStr
      : "-";
  const billingDateStr =
    upcomingInvoice?.date || (renewalDate ? renewalStr : "-");
  const methodStr = activeCard
    ? activeCard.creditCard
      ? `Card •••• ${last4(activeCard.cardNumber)}`
      : `Bank •••• ${last4(activeCard.accountNumber)}`
    : "-";
  // Full card label for the charge-authorization message.
  const cardLabelFull = activeCard
    ? activeCard.creditCard
      ? `${cardBrandLabel(activeCard)} ending in ${last4(activeCard.cardNumber)}`
      : `bank account ending in ${last4(activeCard.accountNumber)}`
    : "your payment method on file";

  // ---- Staff guard (admin-only panel) ----
  if (!isAdmin) {
    return (
      <div className="settings-panel active" data-panel="billing-plan">
        <div className="page-header">
          <div>
            <div className="page-title">Billing &amp; plan</div>
            <div className="page-subtitle">
              Subscription and billing are managed by your administrator.
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-section">
            <div className="row-subtitle">
              You don&apos;t have access to billing settings. Contact an
              administrator on your account.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-panel active" data-panel="billing-plan">
      <div className="page-header">
        <div>
          <div className="page-title">Billing &amp; plan</div>
          <div className="page-subtitle">
            Subscription, payment methods, and upcoming invoice.
          </div>
        </div>
      </div>

      {/* ── Active subscription (hero) ── */}
      <div className="card bill-hero">
        <div className="card-section bill-hero-head">
          <div className="bill-hero-main">
            <div className="bill-eyebrow">Current plan</div>
            <div className="bill-plan-line">
              <span className="bill-plan-name">{planName}</span>
              <span className="bill-cycle-pill">{cycleLabel}</span>
              <span
                className={`badge ${
                  isActive ? "badge-success" : "badge-warn"
                }`}
              >
                {isActive ? "Active" : "Inactive"}
              </span>
            </div>
            {planCost != null ? (
              <div className="bill-price-line">
                <span className="bill-price">{planCostStr}</span>
                <span className="bill-per">/{periodSuffix}</span>
              </div>
            ) : null}
          </div>
          <button
            className="btn"
            id="open-update-plan"
            onClick={openPlanModal}
          >
            Update plan
          </button>
        </div>
        {/* Upcoming invoice — folded into the active subscription card */}
        <div className="card-section">
          <div className="info-grid">
            <div className="info-row">
              <div className="info-label">Next payment</div>
              <div className="info-value bill-stat-strong">
                {loadingAccounts ? (
                  <span className="skl-line" style={{ width: 90, height: 16 }} />
                ) : (
                  nextPaymentStr
                )}
              </div>
            </div>
            <div className="info-row">
              <div className="info-label">Billing date</div>
              <div className="info-value">
                {loadingAccounts ? <span className="skl-line" style={{ width: 110 }} /> : billingDateStr}
              </div>
            </div>
            <div className="info-row">
              <div className="info-label">Payment method</div>
              <div className="info-value">
                {loadingAccounts ? <span className="skl-line" style={{ width: 130 }} /> : methodStr}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Subscription details / add-ons ── */}
      <div className="card">
        <div
          className="card-section"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div className="card-title">Subscription Details</div>
            <div className="card-subtitle">Add-ons attached to this plan.</div>
          </div>
          <button
            className="btn sm"
            aria-expanded={detailsOpen}
            onClick={() => setDetailsOpen((o) => !o)}
          >
            {detailsOpen ? "Hide details" : "View details"}
          </button>
        </div>
        {detailsOpen ? (
        <div className="card-section" style={{ padding: 0 }}>
          <div className="addon-list">
            <div className="addon-row addon-row-base">
              <div>
                <div className="addon-name">
                  {planName} ({cycleLabel})
                </div>
                <div className="addon-sub">Base plan</div>
              </div>
              <div className="addon-price">
                {planCostStr}
                {planCost != null ? ` / ${subType === "year" ? "yr" : "mo"}` : ""}
              </div>
            </div>

            <div className="addon-cat">Apps</div>

            <div className="addon-row">
              <div>
                <div className="addon-name">
                  SkAi (AI assist)
                  <span className="addon-chip">
                    {skaiUnlimited
                      ? "Unlimited"
                      : `${skaiTokenBalance.toLocaleString()} tokens`}
                  </span>
                </div>
                <div className="addon-sub">
                  {skaiActive
                    ? "Unlimited monthly add-on"
                    : skaiPendingCancel
                    ? `Unlimited until ${skaiAccessUntil ? new Date(skaiAccessUntil).toLocaleDateString() : "period end"} · cancellation scheduled`
                    : skaiUnlimited
                    ? "Included with the Gold plan"
                    : skaiTokenBalance > 0
                    ? "One-time tokens · top up anytime"
                    : "Pay as you go · buy tokens as needed"}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                {skaiActive ? (
                  <>
                    <div className="addon-price">${skaiMonthlyPrice ?? 0} / mo</div>
                    <button
                      className="btn sm danger-ghost"
                      onClick={cancelSkai}
                      disabled={skaiCancelLoading}
                      title="Cancel the monthly SkAi add-on"
                    >
                      {skaiCancelLoading ? "Cancelling…" : "Cancel"}
                    </button>
                  </>
                ) : skaiPendingCancel ? (
                  <span style={{ fontSize: 13, color: "#B42318", whiteSpace: "nowrap" }}>
                    Cancels {skaiAccessUntil ? `on ${new Date(skaiAccessUntil).toLocaleDateString()}` : "at period end"}
                  </span>
                ) : isGoldPlan ? (
                  <span className="badge badge-success">Included</span>
                ) : (
                  <div className="addon-price">$0 / mo</div>
                )}
              </div>
            </div>

            <div className="addon-row">
              <div>
                <div className="addon-name">EVV aggregator</div>
                <div className="addon-sub">
                  Electronic Visit Verification submission
                </div>
              </div>
              {isGoldPlan ? (
                <span className="badge badge-success">Included</span>
              ) : (
                <div className="addon-price">$0 / mo</div>
              )}
            </div>

            {calendarInstalled ? (
              <div className="addon-row">
                <div>
                  <div className="addon-name">Calendar</div>
                  <div className="addon-sub">
                    Drag-and-drop shift scheduling
                  </div>
                </div>
                <div className="addon-price">{fmtMoney(calendarMonthly)} / mo</div>
              </div>
            ) : null}

            <div className="addon-cat">Seats</div>

            {renderSeatRow({
              label: "Additional Staff",
              type: "staff",
              included: planStaffLimit,
              added: addStaffCount,
              perSeat: staffSeatPrice,
              cost: addStaffCost,
            })}

            {renderSeatRow({
              label: "Additional Clients",
              type: "clients",
              included: planClientLimit,
              added: addClientCount,
              perSeat: clientSeatPrice,
              cost: addClientCost,
            })}

            <div className="addon-row addon-total">
              <div className="addon-name">Total</div>
              <div className="addon-price">
                {totalStr} / {periodSuffix}
              </div>
            </div>
          </div>
        </div>
        ) : null}
      </div>

      {/* ── SkAi credits ── */}
      <SkaiCreditsCard
        profile={profile}
        paymentMethod={
          activeCard
            ? {
                brand: cardBrandClass(activeCard),
                brandLabel: cardBrandLabel(activeCard),
                tail: activeCard.creditCard
                  ? last4(activeCard.cardNumber)
                  : last4(activeCard.accountNumber),
                isCard: !!activeCard.creditCard,
              }
            : null
        }
        nextBillingDate={billingDateStr}
      />

      {/* ── Payment method ── */}
      <div className="card">
        <div
          className="card-section"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div className="card-title">Payment method</div>
          <button className="btn primary" onClick={addCard}>
            + Add card
          </button>
        </div>
        <div className="card-section" style={{ padding: 0 }}>
          {loadingAccounts ? (
            <div
              className="pay-row"
              style={{ color: "var(--muted)", fontSize: 13 }}
            >
              Loading payment methods…
            </div>
          ) : (connected || []).length === 0 ? (
            <div
              className="pay-row"
              style={{ color: "var(--muted)", fontSize: 13 }}
            >
              No payment methods on file.
            </div>
          ) : (
            connected.map((data, idx) => {
              const brand = cardBrandClass(data);
              const busy = actionLoading === data.id;
              const labelNum = data?.creditCard
                ? `${cardBrandLabel(data)} ending in ${last4(data?.cardNumber)}`
                : `Bank account ending in ${last4(data?.accountNumber)}`;
              return (
                <div
                  className="pay-row"
                  key={data.id || idx}
                  data-default={data?.isActive ? "true" : undefined}
                >
                  <div className="pay-meta">
                    <div className={`card-brand ${brand}`}>
                      {cardBrandLabel(data)}
                    </div>
                    <div>
                      <div className="pay-title">{labelNum}</div>
                      <div className="pay-sub">
                        {data?.cardName || data?.accountNumber
                          ? data?.cardName ||
                            `Account ${last4(data?.accountNumber)}`
                          : "Saved payment method"}
                      </div>
                    </div>
                  </div>
                  <div className="pay-actions">
                    {data?.isActive ? (
                      <span className="default-pill">Default</span>
                    ) : (
                      <button
                        className="btn sm"
                        disabled={busy}
                        onClick={() => setDefaultCard(data)}
                      >
                        {busy ? "…" : "Set as default"}
                      </button>
                    )}
                    <button
                      className="icon-btn delete-card-btn"
                      aria-label="Delete card"
                      disabled={data?.isActive || busy}
                      title={
                        data?.isActive
                          ? "Set another card as default before deleting"
                          : "Delete card"
                      }
                      onClick={() => removeCard(data)}
                    >
                      <TrashIcon stroke={data?.isActive ? "#737373" : "#B42318"} />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ── Update plan modal ── */}
      <div
        style={{ zIndex: 1000 }}
        className={`modal-overlay${planModalOpen ? " open" : ""}`}
        id="change-plan-modal"
        onClick={(e) => {
          if (e.target === e.currentTarget) closePlanModal();
        }}
      >
        <div className="modal-frame" style={{ maxWidth: 720 }}>
          <div className="modal-head">
            <div>
              <div className="modal-head-title" id="cp-title">
                {planSuccess
                  ? planSuccess.kind === "upgrade"
                    ? "Plan upgraded"
                    : "Plan downgraded"
                  : planConfirm
                  ? `${planConfirm.kind === "downgrade" ? "Confirm downgrade" : "Confirm upgrade"}`
                  : "Update plan"}
              </div>
              <div
                style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}
              >
                {planSuccess ? (
                  "Your subscription has been updated."
                ) : planConfirm ? (
                  "Review the change below, then confirm."
                ) : (
                  <>
                    You&apos;re currently on{" "}
                    <strong style={{ color: "var(--ink)" }}>
                      {planName} ({cycleLabel})
                    </strong>
                    . Pick a new plan to switch.
                  </>
                )}
              </div>
            </div>
            <button
              className="modal-close"
              onClick={closePlanModal}
              aria-label="Close"
            >
              <CloseIcon />
            </button>
          </div>

          <div
            className="modal-body"
            style={{
              background: "var(--app-surface)",
              padding: "20px 22px",
              // Keep the modal one height across the picker and confirm views.
              minHeight: 514,
              boxSizing: "border-box",
              ...(planConfirm || planSuccess
                ? { display: "flex", flexDirection: "column", justifyContent: "center" }
                : {}),
            }}
          >
            {planSuccess ? (
              /* ── Success view (in-modal, on-brand) ── */
              <div style={{ textAlign: "center", padding: "18px 8px", width: "100%" }}>
                <div className="confirm-ic confirm-ic-success">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <div className="confirm-title">
                  {planSuccess.kind === "upgrade"
                    ? `You're now on ${planSuccess.name}`
                    : `Switched to ${planSuccess.name}`}
                </div>
                <div className="confirm-text">
                  {planSuccess.kind === "upgrade"
                    ? `Your ${planSuccess.name} plan (${fmtMoney(
                        Number(planSuccess.cost)
                      )}/${planSuccess.cycle}) is active immediately. Prorated charges apply to this billing cycle.`
                    : `Your plan changes to ${planSuccess.name} (${fmtMoney(
                        Number(planSuccess.cost)
                      )}/${planSuccess.cycle}) at the end of your current billing period. Payment is due then, and you keep your current features until the switch.`}
                </div>
              </div>
            ) : planConfirm ? (
              /* ── Confirm view (in-modal, replaces the picker) ── */
              <div
                style={{
                  textAlign: "center",
                  padding: "18px 8px",
                  width: "100%",
                }}
              >
                <div
                  className={`confirm-ic ${
                    planConfirm.kind === "downgrade"
                      ? "confirm-ic-warn"
                      : "confirm-ic-info"
                  }`}
                >
                  {planConfirm.kind === "downgrade" ? (
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <polyline points="19 12 12 19 5 12" />
                    </svg>
                  ) : (
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="19" x2="12" y2="5" />
                      <polyline points="5 12 12 5 19 12" />
                    </svg>
                  )}
                </div>
                <div className="confirm-title">
                  {planConfirm.kind === "downgrade" ? "Downgrade" : "Upgrade"} to{" "}
                  {planConfirm.target?.name}?
                </div>
                <div className="confirm-text">
                  {planConfirm.kind === "downgrade"
                    ? `Your plan will change to ${planConfirm.target?.name} (${fmtMoney(
                        Number(planConfirm.target?.cost)
                      )}/${
                        planConfirm.subscriptionType === "year" ? "yr" : "mo"
                      }) at the start of your next billing period. You keep your current features until then.`
                    : `You'll move to ${planConfirm.target?.name} (${fmtMoney(
                        Number(planConfirm.target?.cost)
                      )}/${
                        planConfirm.subscriptionType === "year" ? "yr" : "mo"
                      }) right away, and you only pay the prorated difference for the ${
                        planConfirm.remainingDays != null
                          ? `${planConfirm.remainingDays} `
                          : ""
                      }day${
                        planConfirm.remainingDays === 1 ? "" : "s"
                      } left in this billing period. The full rate starts next cycle.`}
                </div>
                {planConfirm.kind !== "downgrade" ? (
                  planConfirm.proratedLoading ? (
                    <div className="confirm-prorate">
                      <span className="confirm-prorate-label">Due today (prorated)</span>
                      <span className="confirm-prorate-calc">Calculating…</span>
                    </div>
                  ) : planConfirm.prorated != null && planConfirm.breakdown ? (
                    <div className="confirm-breakdown">
                      <div className="confirm-bd-row">
                        <span>Credit for unused {planName} time</span>
                        <span className="confirm-bd-credit">
                          −{fmtMoney(planConfirm.breakdown.credit)}
                        </span>
                      </div>
                      <div className="confirm-bd-row">
                        <span>{planConfirm.target?.name} for the rest of this period</span>
                        <span>{fmtMoney(planConfirm.breakdown.charge)}</span>
                      </div>
                      <div className="confirm-bd-row confirm-bd-total">
                        <span>Due today</span>
                        <span>{fmtMoney(planConfirm.prorated)}</span>
                      </div>
                    </div>
                  ) : planConfirm.prorated != null ? (
                    <div className="confirm-prorate">
                      <span className="confirm-prorate-label">Due today (prorated)</span>
                      <span className="confirm-prorate-amount">
                        {fmtMoney(planConfirm.prorated)}
                      </span>
                    </div>
                  ) : (
                    <div className="confirm-text" style={{ marginTop: 10 }}>
                      You'll be charged only the prorated difference for the rest of this
                      billing period when you confirm.
                    </div>
                  )
                ) : null}
                {(() => {
                  // Downgrading below a top-tier plan strips white-label /
                  // Design features. Warn when branding or a custom theme is
                  // actually in use so the user knows what they lose.
                  if (planConfirm.kind !== "downgrade") return null;
                  const targetTop = TOP_TIER_PLANS.includes(
                    String(planConfirm.target?.name || "").toLowerCase()
                  );
                  const wl = profile?.business?.whiteLabel;
                  const brandingActive =
                    !!(wl && (wl.enabled || wl.primaryColor)) ||
                    appTheme.get().key !== DEFAULT_THEME_KEY;
                  if (targetTop || !brandingActive) return null;
                  return (
                    <div className="confirm-agree">
                      <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="var(--app-ink-amber)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                        <line x1="12" y1="9" x2="12" y2="13" />
                        <line x1="12" y1="17" x2="12.01" y2="17" />
                      </svg>
                      <span>
                        Downgrading removes all white label features, including
                        custom themes and system designs. Your workspace will
                        revert to the Ogiso standard design.
                      </span>
                    </div>
                  );
                })()}
                {planPayConfirm ? (
                  <div className="confirm-agree">
                    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="#B26B00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <span>
                      {planConfirm.kind === "downgrade"
                        ? `No charge today. Your plan changes to ${planConfirm.target?.name} on ${renewalStr}, and you keep your current features until then. Proceed?`
                        : (
                          <>
                            Your{" "}
                            <strong>{cardLabelFull}</strong> will be charged{" "}
                            <strong>
                              {planConfirm.prorated != null
                                ? fmtMoney(planConfirm.prorated)
                                : "the prorated difference"}
                            </strong>{" "}
                            now. Do you want to proceed?
                          </>
                        )}
                    </span>
                  </div>
                ) : null}
              </div>
            ) : (
            <>
            {/* Billing cycle */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 14,
              }}
            >
              <div className="label" style={{ margin: 0 }}>
                Billing cycle
              </div>
              <div className="segmented" id="cp-cycle">
                <button
                  type="button"
                  className={cycle === "month" ? "active" : ""}
                  onClick={() => setCycle("month")}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  className={cycle === "year" ? "active" : ""}
                  onClick={() => setCycle("year")}
                >
                  Yearly · save 20%
                </button>
              </div>
            </div>

            {/* Plan picker */}
            <div className="cp-plan-grid">
              {modalPlans.length === 0 ? (
                <div
                  style={{
                    padding: "24px",
                    textAlign: "center",
                    color: "var(--muted)",
                    fontSize: 13,
                  }}
                >
                  No plans available for this billing cycle.
                </div>
              ) : null}
              {modalPlans
                .map((plan, sortedIdx) => ({ plan, sortedIdx }))
                .sort(
                  (a, b) =>
                    (a.plan.id === activeId?.id ? 0 : 1) -
                    (b.plan.id === activeId?.id ? 0 : 1)
                )
                .map(({ plan, sortedIdx }) => {
                const visual = planVisual(sortedIdx, modalPlans.length);
                const isCurrent = plan.id === activeId?.id;
                const isChecked = plan.id === selectedPlanId;
                // The current plan is pinned to the top as the big card; the rest
                // are compact rows. Selecting a plan just highlights its border.
                const isHero = isCurrent;
                const perSuffix = plan.duration === "year" ? "/yr" : "/mo";
                let tag = null;
                if (!isCurrent && activeId) {
                  tag =
                    Number(plan.cost) < Number(activeId?.cost)
                      ? { text: "Downgrade", color: "#B42318" }
                      : { text: "Upgrade", color: "#027A48" };
                }
                return (
                  <label
                    key={plan.id}
                    className={`cp-plan-option${isHero ? " hero" : " compact"}${
                      isCurrent ? " current" : ""
                    }`}
                    data-plan={plan.name}
                  >
                    <input
                      type="radio"
                      name="cp-plan"
                      value={plan.id}
                      checked={isChecked}
                      onChange={() => setSelectedPlanId(plan.id)}
                    />
                    <div className="cp-plan-row">
                      <div className="cp-plan-head">
                        <div className={`plan-ico ${visual.tone}`}>
                          <PlanIcon glyph={visual.glyph} />
                        </div>
                        <div>
                          <div className="cp-plan-name">
                            {plan.name}
                            {isCurrent ? (
                              <span className="cp-current-pill">Current plan</span>
                            ) : null}
                          </div>
                          <div className="cp-plan-feats">
                            {planFeatures(plan)}
                          </div>
                        </div>
                      </div>
                      <div className="cp-plan-price-block">
                        <div className="cp-plan-price">
                          {fmtMoney(Number(plan.cost))}
                          <span>{perSuffix}</span>
                        </div>
                        {tag ? (
                          <div
                            className="cp-plan-tag"
                            style={{ color: tag.color }}
                          >
                            {tag.text}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>

            {/* Cost preview — proration is computed server-side by the
                existing upgrade flow, so we do not fabricate amounts here.
                We only confirm the switch is effective immediately. */}
            {planChanged ? (
              <div style={{ marginTop: 16 }}>
                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    padding: "10px 12px",
                    background: "#EFF8FF",
                    border: "1px solid #B2DDFF",
                    borderRadius: 10,
                    fontSize: 12.5,
                    color: "#175CD3",
                  }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ flexShrink: 0, marginTop: 1 }}
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                  <div>
                    Plan change is <strong>effective immediately</strong>.
                    Exact proration is calculated at the next step.
                    {renewalDate ? (
                      <>
                        {" "}
                        Your monthly subscription charge will be on{" "}
                        <strong>{renewalStr}</strong>.
                      </>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : null}
            </>
            )}
          </div>

          <div
            className="card-footer"
            style={{ padding: "14px 22px", ...(planSuccess ? { justifyContent: "flex-end" } : {}) }}
          >
            {planSuccess ? (
              <button className="btn primary" onClick={closePlanModal}>
                Done
              </button>
            ) : planConfirm ? (
              <>
                <button
                  className="btn"
                  onClick={() =>
                    planPayConfirm ? setPlanPayConfirm(false) : setPlanConfirm(null)
                  }
                  disabled={planChangeLoading}
                >
                  {planPayConfirm ? "Cancel" : "Back"}
                </button>
                <button
                  className="btn primary"
                  onClick={() =>
                    planPayConfirm ? runPlanChange() : setPlanPayConfirm(true)
                  }
                  disabled={planChangeLoading || planConfirm.proratedLoading}
                  style={
                    planChangeLoading || planConfirm.proratedLoading
                      ? { opacity: 0.5 }
                      : undefined
                  }
                >
                  {planChangeLoading
                    ? "Processing…"
                    : planPayConfirm
                    ? planConfirm.kind === "downgrade"
                      ? "Confirm downgrade"
                      : `Confirm & pay ${
                          planConfirm.prorated != null
                            ? fmtMoney(planConfirm.prorated)
                            : "now"
                        }`
                    : planConfirm.kind === "downgrade"
                    ? "Downgrade now"
                    : "Upgrade now"}
                </button>
              </>
            ) : (
              <>
                <button className="btn" onClick={closePlanModal}>
                  Cancel
                </button>
                <button
                  className="btn primary"
                  id="cp-confirm"
                  disabled={!planChanged || planChangeLoading}
                  style={
                    !planChanged || planChangeLoading
                      ? { opacity: 0.5 }
                      : undefined
                  }
                  title={
                    !planChanged
                      ? "Select a different plan to continue"
                      : undefined
                  }
                  onClick={confirmPlanChange}
                >
                  Confirm change
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Add-card modal (Stripe — /payment/create-new-card) ── */}
      <div
        style={{ zIndex: 1000 }}
        className={`modal-overlay${cardModalOpen ? " open" : ""}`}
        onClick={(e) => {
          if (e.target === e.currentTarget) setCardModalOpen(false);
        }}
      >
        <div className="modal-frame" style={{ maxWidth: 480 }}>
          <div className="modal-head">
            <div className="modal-head-title">Add new card</div>
            <button
              type="button"
              className="modal-close"
              aria-label="Close"
              onClick={() => setCardModalOpen(false)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#525252" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          <div className="modal-body" style={{ background: "var(--app-surface)", padding: "18px 22px" }}>
            <div style={{ marginBottom: 12 }}>
              <label className="label">Name on card</label>
              <input className="input" value={card.name} onChange={setCardField("name")} placeholder="e.g. Frank Aikhu" />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label className="label">Card number</label>
              <input className="input" value={card.cardNumber} onChange={setCardField("cardNumber")} placeholder="1234 5678 9012 3456" inputMode="numeric" />
            </div>
            <div className="form-grid" style={{ gridTemplateColumns: "1fr 1fr 1fr", marginBottom: 18 }}>
              <div>
                <label className="label">Month</label>
                <input className="input" value={card.cardMonth} onChange={setCardField("cardMonth")} placeholder="MM" inputMode="numeric" maxLength={2} />
              </div>
              <div>
                <label className="label">Year</label>
                <input className="input" value={card.cardYear} onChange={setCardField("cardYear")} placeholder="YY" inputMode="numeric" maxLength={2} />
              </div>
              <div>
                <label className="label">CVV</label>
                <input className="input" value={card.CVV} onChange={setCardField("CVV")} placeholder="•••" inputMode="numeric" maxLength={4} />
              </div>
            </div>
            <button
              type="button"
              className="btn primary"
              style={{ width: "100%", justifyContent: "center" }}
              onClick={handleAddCard}
              disabled={connectingCard}
            >
              {connectingCard ? "Connecting…" : "Connect"}
            </button>
            <div style={{ fontSize: 12, color: "var(--muted)", textAlign: "center", marginTop: 10 }}>
              Securely processed by Stripe.
            </div>
          </div>
        </div>
      </div>

      {skaiCancelModal}
    </div>
  );
}
